/**
 * Cloud VM in Add machine (ADR 0105 decision 1): a name, one button, and the cloud-init user
 * data that makes a new Linux VM install the agent and join by itself. The document carries a
 * one-time join ticket, so it is shown once, like the join link. Lives in the command-line
 * disclosure and follows the join link flag (lands dark with it).
 */
import { Cloud } from '@orgus/ui/icons';
import { Button, CopyButton, ErrorState, Input, RowStatus } from '@orgus/ui';
import { CLOUD_INIT_SENTENCE, CLOUD_USER_DATA_FIELDS } from '@orgus/protocol';
import { useState } from 'react';
import { CLOUD_INIT_COPIED, CLOUD_INIT_NOT_COPIED } from './joinTickets.ts';
import type { JoinTicketsState } from './useJoinTickets.ts';

export function CloudVmPanel({ s, hubUrl }: { s: Pick<JoinTicketsState, 'on' | 'cloud' | 'makeCloud' | 'copyCloud'>; hubUrl?: string }) {
  const [name, setName] = useState('');
  const [repo, setRepo] = useState('');
  if (!s.on) return null;
  const { cloud } = s;
  const make = () => void s.makeCloud({ ...(name.trim() ? { name: name.trim() } : {}), ...(repo.trim() ? { repoUrl: repo.trim() } : {}), ...(hubUrl ? { hubUrl } : {}) });
  return (
    <section aria-label="Cloud VM" className="flex flex-col gap-3" data-testid="cloud-vm">
      <p className="text-caption text-fg-subtle">{CLOUD_INIT_SENTENCE}</p>
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-end sm:gap-3">
        <Input
          label="Machine name"
          placeholder="Picked for you if empty"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="sm:w-64"
          data-testid="cloud-vm-name"
        />
        <Input
          label="Repository"
          placeholder="Optional, https://github.com/org/repo"
          value={repo}
          onChange={(e) => setRepo(e.target.value)}
          className="sm:w-72"
          data-testid="cloud-vm-repo"
        />
        <Button variant="secondary" icon={<Cloud />} loading={cloud.kind === 'making'} onClick={make} data-testid="cloud-vm-make">
          Make cloud-init
        </Button>
      </div>
      {cloud.kind === 'making' && (
        <div role="status" aria-live="polite">
          <RowStatus tone="working" icon="working" label="Making the cloud-init document" />
        </div>
      )}
      {cloud.kind === 'error' && <ErrorState variant="inline" error={cloud.error} onRetry={make} />}
      {cloud.kind === 'ready' && (
        <div className="flex flex-col gap-2" data-testid="cloud-vm-made">
          <p className="text-ui text-fg-muted" role="status" aria-live="polite">
            {cloud.copied ? CLOUD_INIT_COPIED : CLOUD_INIT_NOT_COPIED}
          </p>
          <div className="flex items-start gap-2 rounded-card border border-border bg-bg py-2 pl-3 pr-1">
            <pre className="min-w-0 flex-1 select-all overflow-auto py-1 font-mono text-caption text-fg max-h-64" aria-label="Cloud-init user data">
              {cloud.userData}
            </pre>
            <CopyButton text={cloud.userData} label="Copy cloud-init" size="sm" />
          </div>
          <ul className="flex flex-col gap-1 text-caption text-fg-subtle" aria-label="Where the user data goes">
            {CLOUD_USER_DATA_FIELDS.map((f) => (
              <li key={f.provider}>
                <span className="text-fg-muted">{f.provider}:</span> {f.field}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
